import {
  PlanningInfo,
  TransactionAndAuthorizationOnTrip,
  triggerToastDS,
  VehicleType,
} from "@qivia/ui";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HoursBarTripsOrPlanningVertical } from "@qivia/ui/src/designSystem/components/DayTripComponent/HoursBarTripsOrPlanningVertical";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import {
  geolocationSlice,
  requestTripAddressesAsync,
  selectRequestTripAddressesStatus,
  selectTripUuidRequestingAddresses,
} from "../../../geolocation/geolocationSlice";
import { selectCompany } from "../../../homeSlice";
import { TRIP_ADDRESSES_ALLOWED_COMPANY_UUIDS } from "../../trips/tripLib";

export type DrivingEvent = {
  startDate: string;
  endDate: string;
  eventType: string;
  unit: string | null;
  value: number | null;
};

export interface Trip {
  uuid: string;
  startDate: string;
  endDate: string;
  distance: number | null;
  ecoDrivingScore: number | null;
  smoothnessScore: number | null;
  harshAccelerationsCount: number | null;
  harshBrakesCount: number | null;
  sharpTurnsCount: number | null;
  departureAddress: string | null;
  destinationAddress: string | null;
}

export interface TripOrPlanningOptionsDisplay {
  distance: number | null;
  duration: number | null;
  startDate: string;
  endDate: string;
  ecoDrivingScore?: number | null;
  // harshAccelerationsCount: number | null;
  // harshBrakesCount: number | null;
  // sharpTurnsCount: number | null;
  driver: string | null;
  clientName?: string | null;
  clientCity?: string | null;
  hasKmClient?: boolean;
  activityType?: string | null;
  isOutIntervention?: boolean;
}

export interface VehicleInfo {
  registrationNumber: string | null;
  reference: string | null;
  vehicleType: VehicleType;
  model: string | null;
}

export interface DayTripOrPlanningDetailsProps {
  trips: Trip[];
  driverName: string | null;
  plannings: PlanningInfo[];
  transactionsAndAuth: TransactionAndAuthorizationOnTrip[];
}

const removeSecondsFromDate = (date: string): string => {
  return new Date(
    new Date(date).setHours(
      new Date(date).getHours(),
      new Date(date).getMinutes(),
      0,
      0,
    ),
  ).toISOString();
};

const getAllTransactionDisplayedFilteredDuplicateIn20Minutes = (
  transactionsAndAuthDates: TransactionAndAuthorizationOnTrip[],
) => {
  if (transactionsAndAuthDates.length === 0) return [];
  const sortedDates = transactionsAndAuthDates.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
  return sortedDates.reduce(
    (
      acc: TransactionAndAuthorizationOnTrip[],
      current: TransactionAndAuthorizationOnTrip,
    ) => {
      if (
        acc.length === 0 ||
        (new Date(current.date).getTime() -
          new Date(acc[acc.length - 1].date).getTime()) /
          (1000 * 60) >=
          20
      ) {
        acc.push(current);
      }
      return acc;
    },
    [],
  );
};

export const DayTripPlanningDetails = (
  props: DayTripOrPlanningDetailsProps,
) => {
  const { trips, driverName, plannings, transactionsAndAuth } = props;
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const { t } = useTranslation();
  const requestTripAddressesStatus = useAppSelector(
    selectRequestTripAddressesStatus,
  );
  const tripUuidRequestingAddresses = useAppSelector(
    selectTripUuidRequestingAddresses,
  );

  useEffect(() => {
    if (
      requestTripAddressesStatus !== "success" &&
      requestTripAddressesStatus !== "failed"
    ) {
      return;
    }

    if (requestTripAddressesStatus === "success") {
      triggerToastDS(t("geolocation.requestTripAddresses.success"), "SUCCESS");
    }

    if (requestTripAddressesStatus === "failed") {
      triggerToastDS(t("geolocation.requestTripAddresses.error"), "ERROR");
    }

    void dispatch(geolocationSlice.actions.resetRequestTripAddressesStatus());
  }, [dispatch, requestTripAddressesStatus, t]);

  const transactionsToDisplay =
    getAllTransactionDisplayedFilteredDuplicateIn20Minutes(transactionsAndAuth);

  const sortTrips = [...trips]
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
    )
    .map((t) => {
      return {
        ...t,
        startDate: removeSecondsFromDate(t.startDate),
        endDate: removeSecondsFromDate(t.endDate),
      };
    });

  const sortPlannings = [...plannings]
    .sort(
      (a, b) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )
    .map((p) => {
      return {
        ...p,
        startTime: removeSecondsFromDate(p.startTime),
        endTime: removeSecondsFromDate(p.endTime),
        isOutIntervention: !p.clientCity && !p.clientName,
      };
    });

  // This data is from a csv where it have a special line for the kmClient
  // If we have a kmClient, we need to get the planning setted in the previous line
  sortPlannings.forEach((planning, index) => {
    if (index > 0) {
      if (planning.hasKmClient === true)
        sortPlannings[index - 1].hasKmClient = true;
    }
  });

  return (
    <HoursBarTripsOrPlanningVertical
      trips={sortTrips}
      driverName={driverName}
      plannings={sortPlannings}
      transactionsToDisplay={transactionsToDisplay}
      tripUuidWaitingForAddresses={tripUuidRequestingAddresses}
      canRequestTripAddresses={
        !!company && TRIP_ADDRESSES_ALLOWED_COMPANY_UUIDS.includes(company.uuid)
      }
      onRequestTripAddresses={(trip) =>
        void dispatch(
          requestTripAddressesAsync({
            tripUuid: trip.uuid,
            date: trip.startDate,
          }),
        )
      }
    />
  );
};
